import { format } from 'date-fns';
import type { Locale } from 'date-fns';

import type { TValidImageType } from './visitAttachmentsTypes';
import {
  IMAGE_TYPE_ABBREVIATIONS,
  VALID_IMAGE_TYPES,
} from './visitAttachmentsTypes';

/**
 * Format a date string using the provided format
 */
export const formatDate = (
  dateString: string,
  formatStr: string,
  locale?: Locale,
): string => {
  try {
    const date = new Date(dateString);
    return locale
      ? format(date, formatStr, { locale })
      : format(date, formatStr);
  } catch (e) {
    return dateString;
  }
};

/**
 * Format a visit date in full format (e.g., "January 1, 2023")
 */
export const formatVisitDate = (
  date: Date | string | null,
  locale: Locale,
): string => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  return format(dateObj, 'MMM d, yyyy', { locale });
};

/**
 * Format a visit date in compact format (e.g., "01/01/2023")
 */
export const formatVisitDateCompact = (
  dateString: string,
  locale?: Locale,
): string => formatDate(dateString, 'MM/dd/yyyy', locale);

/**
 * Get the count of valid images for a visit
 */
export const getImageCounts = (
  imageTypeCounts: { type: string; count: number }[],
): number => {
  return imageTypeCounts
    .filter((t) => VALID_IMAGE_TYPES.includes(t.type as TValidImageType))
    .reduce((sum, t) => sum + t.count, 0);
};

/**
 * Get a string of abbreviations for image types present in a visit
 */
export const getImageTypeAbbreviations = (
  imageTypeCounts: { type: string; count: number }[],
): string => {
  return imageTypeCounts
    .filter(
      (t) =>
        VALID_IMAGE_TYPES.includes(t.type as TValidImageType) && t.count > 0,
    )
    .map((t) => IMAGE_TYPE_ABBREVIATIONS[t.type] || t.type)
    .join(', ');
};
